import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { Tissue } from "../../../../models";
import { DataTable, fas, Icon } from "../../../../component-library";
import { InvoiceDashboardViews } from ".";
import { Link } from "react-router-dom";
import { displayDate, getReasonsTissueNotReadyForInvoice } from "../../../../helper-functions";
import { Lists } from "../../../lists";

interface InvoicingDashboardTissuesProps {
    tissues: Tissue[];
    view: InvoiceDashboardViews;
}

export function InvoicingDashboardTissues({ tissues, view }: InvoicingDashboardTissuesProps) {
    const isReady = view === InvoiceDashboardViews.TissuesReady;
    const list = isReady ? Lists.TissuesReadyToInvoice : Lists.TissuesNotReadyToInvoice;

    const columns = useMemo<ColumnDef<Tissue>[]>(() => {
        const result: ColumnDef<Tissue>[] = [
            {
                header: "Tissue",
                accessorFn: (tissue) => tissue.number,
                cell: ({ row: { original: tissue } }) => {
                    return (
                        <>
                            {!isReady && (
                                <Icon
                                    icon={fas.faInfoCircle}
                                    title={getReasonsTissueNotReadyForInvoice(tissue).join("\n")}
                                    aria-label={getReasonsTissueNotReadyForInvoice(tissue).join(" ")}
                                    className="me-1 text-primary"
                                />
                            )}
                            <Link to={`/lists/${list}/tissues/${tissue.id}/placement`}>{tissue.number}</Link>
                        </>
                    );
                },
            },
            {
                header: "Billing Account",
                accessorFn: (tissue) => {
                    const surgery = tissue.surgery ?? tissue.backupSurgery;
                    return surgery?.billingAccount?.name;
                },
                cell: ({ row: { original: tissue } }) => {
                    const surgery = tissue.surgery ?? tissue.backupSurgery;
                    if (surgery?.billingAccount) {
                        return <Link to={`/accounts/${surgery.billingAccount.id}`}>{surgery.billingAccount.name}</Link>;
                    }
                    return null;
                },
            },
            {
                header: "Surgeon",
                accessorFn: (tissue) => {
                    const surgery = tissue.surgery ?? tissue.backupSurgery;
                    if (surgery?.surgeon) {
                        return `${surgery.surgeon.firstName} ${surgery.surgeon.lastName}`;
                    }
                    return "";
                },
                cell: ({ row: { original: tissue } }) => {
                    const surgery = tissue.surgery ?? tissue.backupSurgery;
                    const name = surgery?.surgeon ? `${surgery.surgeon.firstName} ${surgery.surgeon.lastName}` : "";
                    if (surgery) {
                        return <Link to={`/lists/none/surgeries/${surgery.id}`}>{name}</Link>;
                    }
                    return <span>{name}</span>;
                },
            },
            {
                header: "Surgery Date",
                accessorFn: (tissue) => {
                    const surgery = tissue.surgery ?? tissue.backupSurgery;
                    return displayDate(surgery?.scheduledOnUtc);
                },
            },
            {
                header: "Recipient",
                accessorFn: (tissue) => {
                    const surgery = tissue.surgery ?? tissue.backupSurgery;
                    if (surgery) {
                        return `${surgery.recipientFirstName || ""} ${surgery.recipientLastName || ""}`.trim();
                    }
                    return "";
                },
            },
            {
                header: "Order",
                accessorFn: (tissue) => tissue.orders?.[0]?.id,
                cell: ({ row: { original: tissue } }) => {
                    if (tissue.orders?.[0]) {
                        return (
                            <Link to={`/lists/none/orders/${tissue.orders[0].id}`}>Order #{tissue.orders[0].id}</Link>
                        );
                    }
                    return null;
                },
            },
        ];

        if (isReady) {
            result.push({
                header: "",
                id: "actions",
                cell: ({ row: { original: tissue } }) => (
                    <div className="text-end">
                        <Link
                            to={`/create-invoice?tissueId=${tissue.id}&surgeryId=${tissue.surgery?.id ?? ""}`}
                            className="btn btn-primary btn-sm"
                        >
                            Create Invoice
                        </Link>
                    </div>
                ),
            });
        }

        return result;
    }, [isReady, list]);

    if (!tissues.length) {
        return <div>No tissues found</div>;
    }

    return <DataTable columns={columns} data={tissues} paginated={tissues.length > 10} showGlobalFilter />;
}
